import { organizationRoleLabel, organizationRoleLabelFa } from './organization';

const BLOCKED_MEMBER_ROLE_SLUGS = new Set([
  'super_admin',
  'superadmin',
  'admin',
  'manager',
  'owner',
]);

const ASSIGNABLE_SYSTEM_ROLE_SLUGS = new Set([
  'organization_manager',
  'meeting_manager',
  'room_manager',
  'organization_finance_manager',
  'member',
  'observer',
  'guest',
]);

const CUSTOMIZABLE_SYSTEM_ROLE_SLUGS = new Set([
  'organization_manager',
  'meeting_manager',
  'manager',
  'room_manager',
  'organization_finance_manager',
  'member',
  'observer',
  'guest',
]);

const ORG_MANAGER_CUSTOMIZABLE_ROLE_SLUGS = new Set([
  'meeting_manager',
  'manager',
  'room_manager',
  'organization_finance_manager',
  'member',
  'observer',
  'guest',
]);

const LIMITED_ORG_ROLE_SLUGS = new Set(['member', 'observer', 'guest']);

function normalizeRoleSlug(value) {
  if (value == null || value === '') return '';
  return String(value).toLowerCase().trim().replace(/[\s-]+/g, '_');
}

/** نقش سازمانی کاربر در یک سازمان مشخص */
export function getOrgRole(organizations, orgId) {
  if (!orgId || !Array.isArray(organizations)) return '';
  const org = organizations.find((o) => String(o.id) === String(orgId));
  if (!org) return '';
  return normalizeRoleSlug(organizationRoleLabel(org));
}

export function isOrgOwner(organizations, orgId) {
  return getOrgRole(organizations, orgId) === 'owner';
}

export function isOrgManager(organizations, orgId) {
  return getOrgRole(organizations, orgId) === 'manager';
}

export function isOrgExecutive(organizations, orgId) {
  const role = getOrgRole(organizations, orgId);
  return role === 'owner' || role === 'organization_manager';
}

/** عضو ساده سازمان (نقش member بدون owner/manager) */
export function isPlainOrgMember(organizations, orgId) {
  return getOrgRole(organizations, orgId) === 'member';
}

/** عضو، مشاهده‌گر و مهمان — بدون منوهای مدیریتی سازمان */
export function isLimitedOrgRole(organizations, orgId) {
  return LIMITED_ORG_ROLE_SLUGS.has(getOrgRole(organizations, orgId));
}

export function roleRequiresExplicitScope(role) {
  if (!role) return false;
  if (role.requires_explicit_scope) return true;
  return ['meeting_manager', 'observer', 'guest', 'manager'].includes(normalizeRoleSlug(role.name || role.slug));
}

export function isGuestRole(role) {
  return normalizeRoleSlug(role?.name || role?.slug) === 'guest';
}

/** نقش‌های قابل انتخاب در فرم افزودن/ویرایش عضو */
export function filterAssignableMemberRoles(roles = []) {
  return roles.filter((r) => {
    const slug = normalizeRoleSlug(r.name || r.slug);
    if (BLOCKED_MEMBER_ROLE_SLUGS.has(slug)) return false;
    if (ASSIGNABLE_SYSTEM_ROLE_SLUGS.has(slug)) return true;
    return !r.is_system;
  });
}

/** شناسه نقش member در لیست نقش‌های سازمان */
export function findMemberRoleId(roles = []) {
  const member = roles.find((r) => normalizeRoleSlug(r.name) === 'member');
  return member?.id ?? undefined;
}

export function formatMemberRoleOption(r) {
  const name = r.name || r.slug || '—';
  return { value: r.id, label: organizationRoleLabelFa(name, r.display_name) };
}

/**
 * آیا کاربر می‌تواند مجوزهای نقش را در سازمان ویرایش کند؟
 */
export function canEditOrgRolePermissions(access, role) {
  if (!role || !access) return false;
  if (access.platformSa) return true;

  const roleName = normalizeRoleSlug(role.name);
  const isSystem = Boolean(role.is_system);

  if (!isSystem) {
    return access.owner || access.rolesManage || access.executive;
  }

  if (access.owner || access.rolesManage) {
    return CUSTOMIZABLE_SYSTEM_ROLE_SLUGS.has(roleName);
  }

  if (access.executive) {
    return ORG_MANAGER_CUSTOMIZABLE_ROLE_SLUGS.has(roleName);
  }

  return false;
}
